import React, { useState, useEffect, useCallback } from 'react'
import { Portal, Paper, Button, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import Text from '../Text'

// Tour leve de coach-marks para a tela de conexão. Recebe passos com um getter do
// elemento-alvo e destaca cada um com um recorte + balão. É totalmente dispensável
// (Pular / Concluir) e reposiciona no scroll/resize. Sem dependência externa —
// usa Portal do MUI e getBoundingClientRect.
export default function ConnectTour({ open, steps, onClose, mandatory }) {
  const theme = useTheme()
  const [i, setI] = useState(0)
  const [rect, setRect] = useState(null)

  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const measure = useCallback(() => {
    const step = steps[i]
    const el = step && step.getEl && step.getEl()
    if (!el || !el.getBoundingClientRect) {
      setRect(null)
      return
    }
    const r = el.getBoundingClientRect()
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom })
  }, [i, steps])

  // reinicia no passo 0 sempre que abre
  useEffect(() => {
    if (open) setI(0)
  }, [open])

  // rola o alvo pra vista, mede e acompanha scroll/resize
  useEffect(() => {
    if (!open) return
    const step = steps[i]
    const el = step && step.getEl && step.getEl()
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' })
    }
    const t = setTimeout(measure, reduce ? 0 : 320)
    const onWin = () => measure()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, i, measure, steps, reduce])

  if (!open) return null

  const step = steps[i] || {}
  const last = i >= steps.length - 1
  const pad = 8
  const zBase = theme.zIndex.modal

  const popW = 320
  const popH = 190
  let popTop = rect ? rect.bottom + 14 : 24
  let popLeft = rect ? Math.min(Math.max(12, rect.left), window.innerWidth - popW - 12) : 24
  if (rect && popTop + popH > window.innerHeight - 12) {
    popTop = Math.max(12, rect.top - popH - 14)
  }

  return (
    <Portal>
      {/* bloqueia interação com o resto da tela durante o tour */}
      <div style={{ position: 'fixed', inset: 0, zIndex: zBase, cursor: 'default' }} />

      {/* recorte iluminado sobre o alvo (o box-shadow gigante escurece o entorno) */}
      {rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 14,
            pointerEvents: 'none',
            zIndex: zBase + 1,
            boxShadow: `0 0 0 9999px ${alpha('#060e0a', 0.62)}, 0 0 0 2px ${theme.palette.primary.main}`,
            transition: reduce ? 'none' : 'all .28s cubic-bezier(.3,.8,.3,1)',
          }}
        />
      )}

      <Paper
        elevation={8}
        style={{
          position: 'fixed',
          top: popTop,
          left: popLeft,
          width: popW,
          maxWidth: 'calc(100vw - 24px)',
          zIndex: zBase + 2,
          padding: 16,
          borderRadius: 14,
          transition: reduce ? 'none' : 'top .28s ease, left .28s ease',
        }}
      >
        <Text
          className="font-black"
          style={{ fontSize: '.68rem', letterSpacing: '.06em', textTransform: 'uppercase', color: theme.palette.primary.main }}
        >
          Passo {i + 1} de {steps.length}
        </Text>
        <Text className="font-bold" style={{ fontSize: '1rem', margin: '4px 0 6px' }}>{step.title}</Text>
        <Text className="text-sm" style={{ color: theme.palette.text.secondary, lineHeight: 1.45 }}>{step.body}</Text>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 5, marginRight: 'auto' }}>
            {steps.map((_, k) => (
              <span
                key={k}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: k === i ? theme.palette.primary.main : theme.palette.divider,
                }}
              />
            ))}
          </div>
          {!mandatory && (
            <Button size="small" color="inherit" onClick={onClose}>Pular</Button>
          )}
          {mandatory && i > 0 && (
            <Button size="small" color="inherit" onClick={() => setI(i - 1)}>Voltar</Button>
          )}
          <Button size="small" variant="contained" onClick={() => (last ? onClose() : setI(i + 1))}>
            {last ? 'Concluir' : 'Próximo'}
          </Button>
        </div>
      </Paper>
    </Portal>
  )
}
